import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type SetQueryParamOptions = {
    replace?: boolean;
};

export const useIntegerQueryParam = (
    name: string
): [
        number | undefined,
        (value: number | undefined, options?: SetQueryParamOptions) => void
] => {
    const [searchParams, setSearchParams] = useSearchParams();

    const value: number | undefined = useMemo((): number | undefined => {
        const queryValue: string | null = searchParams.get(name);

        if (!queryValue) {
            return undefined;
        }

        const parsedValue: number = Number(queryValue);

        return Number.isInteger(parsedValue) && parsedValue > 0
            ? parsedValue
            : undefined;
    }, [name, searchParams]);

    const setValue = useCallback(
        (nextValue: number | undefined, options?: SetQueryParamOptions): void => {
            const nextSearchParams: URLSearchParams = new URLSearchParams(searchParams);

            if (nextValue != null) {
                nextSearchParams.set(name, nextValue.toString());
            } else {
                nextSearchParams.delete(name);
            }

            if (nextSearchParams.toString() === searchParams.toString()) {
                return;
            }

            setSearchParams(nextSearchParams, {
                replace: options?.replace ?? true
            });
        },
        [name, searchParams, setSearchParams]
    );

    return [value, setValue];
};
